import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Paper, TableBody, TableContainer, TableHead, TableRow, Link } from "@mui/material"
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";

//Common styles
import { CustomTableRowTR, HorizontalContainer, ShowValue, StyledTable, TableData, TableHeader } from "../../styles/commonStyles"
import CustomCallIconButton from "../Common/CallButton/CustomCallIconButton";
import SosUpdateDialog from "../Common/SosUpdateDialog/SosUpdateDialog";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";
import { UserType } from "../../enums/common";
import { maskPhoneNumber } from "../../utils/PhoneNumberUtils";

const SosAlertList = ({
    data,
    loading,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    onRefresh,
}: {
    data: any[];
    loading: boolean;
    page: number;
    rowsPerPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rows: number) => void;
    onRefresh?: () => void;
}) => {
    const navigate = useNavigate();
    const [selectedRow, setSelectedRow] = useState<any | null>(null);

    const columns: CommonListColumn[] = [
        {
            label: "Actions",
            stopRowClick: true,
            render: (row) => (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSelectedRow(row)}
                >
                    Update
                </Button>
            ),
        },
        { label: "Id", field: "id" },
        { label: "Status", field: "status", type: "status" },
        {
            label: "ReferenceId",
            render: (row) => (
                <Link
                    href={`/#/${row?.refernceModelName === "ONE_MEAL_SERVICE" ? 'OneMealService' : 'ChefForParty'}/${row?.referenceId}/show`}
                    underline="hover"
                >
                    {row?.referenceId || "-"}
                </Link>
            ),
        },
        {
            label: "Reference Model",
            render: (row) => (row?.refernceModelName === "ONE_MEAL_SERVICE" ? "Chefit" : "Party"),
        },
        {
            label: "Cook",
            stopRowClick: true,
            render: (row) => (
                <div>
                    <ShowValue style={{ marginBottom: 4 }}>
                        {row?.cookId ? (
                            <Link href={`/#/Cooks/${row.cookId}/show`} underline="hover">
                                {[row?.cookProfile?.firstName, row?.cookProfile?.lastName]
                                    .filter(Boolean)
                                    .join(" ") || `Cook #${row.cookId}`}
                            </Link>
                        ) : (
                            "-"
                        )}
                    </ShowValue>
                    <HorizontalContainer>
                        {row?.cookProfile?.mobile ? (
                            <CustomCallIconButton
                                color="#6C7C93"
                                size={18}
                                toPhoneNumber={row.cookProfile.mobile}
                                userType={UserType.COOK}
                                referenceId={Number(row?.referenceId)}
                                referenceModelName={row?.refernceModelName}
                            />
                        ) : null}
                        <ShowValue style={{ marginBottom: 0 }}>
                            {maskPhoneNumber(row?.cookProfile?.mobile || "") || "-"}
                        </ShowValue>
                    </HorizontalContainer>
                </div>
            ),
        },
        { label: "Alert Reason", field: "queryDetails.trEn" },
        { label: "QueryDesc", field: "queryDesc" },
        { label: "Alert Created At", field: "createdAt", type: "datetime" },
        { label: "Status Last Updated At", field: "lastUpdatedAt", type: "datetime" },
        {
            label: "Comments",
            stopRowClick: true,
            render: (row) =>
                row?.comments?.length ? (
                    <TableContainer component={Paper} sx={{ borderRadius: "8px", minWidth: 360 }}>
                        <StyledTable size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "rgba(232, 235, 237, 0.3) !important" }}>
                                    <TableHeader>Comment</TableHeader>
                                    <TableHeader>Commenter email</TableHeader>
                                    <TableHeader>Timestamp</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {row.comments.map((each: any) => (
                                    <CustomTableRowTR key={each.id}>
                                        <TableData>{each?.comment}</TableData>
                                        <TableData>{each?.commenterEmail}</TableData>
                                        <TableData>
                                            {each?.timestamp ? formatDateTimeWithSeconds(each?.timestamp) : "-"}
                                        </TableData>
                                    </CustomTableRowTR>
                                ))}
                            </TableBody>
                        </StyledTable>
                    </TableContainer>
                ) : (
                    <span>Not Available</span>
                ),
        },
    ];

    return (
        <>
            <CommonListTable
                title="Sos Alerts"
                columns={columns}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                maxTableHeight="calc(100vh - 340px)"
                onRowClick={(row: any) => {
                    sessionStorage.setItem(
                        "lastVisitedSosId",
                        String(row?.id)
                    );
                    navigate(
                        `/SosAlerts/${row?.refernceModelName}/${row?.id}`
                    );
                }}
            />

            {selectedRow && (
                <SosUpdateDialog
                    open={!!selectedRow}
                    onClose={() => setSelectedRow(null)}
                    sosId={Number(selectedRow.id)}
                    currentStatus={selectedRow.status}
                    referenceModelName={selectedRow.refernceModelName}
                    alertReason={selectedRow?.queryDetails?.trEn}
                    onSuccess={onRefresh}
                />
            )}
        </>
    )
}

export default SosAlertList;
